import type { MouseEvent } from 'react';
import { LogOut, PenLine } from 'lucide-react';
import { Badge, Drawer, cn } from '@eombor/ui';
import { t } from '../../shared/i18n/index.js';
import { activeNavTo, isPlainLeftClick, navEntry, visibleNavItems } from './nav-model.js';

export interface MobileNavDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Ruxsat tekshiruvi — odatda `usePermission()` dan keladi. */
  can: (permission: string) => boolean;
  activePath?: string;
  badges?: Readonly<Record<string, number>>;
  hasSubdivision?: boolean;
  onNavigate?: (to: string) => void;
  /** Xodimning ismi; sessiya hali tiklanmagan bo'lsa `null`. */
  userName: string | null;
  userRole: string;
  onOpenSignature: () => void;
  onLogout: () => void;
}

/**
 * Telefondagi TO'LIQ menyu — pastki qatordagi «Menyu» ochadi.
 *
 * `@eombor/ui` dagi `Drawer` ustiga quriladi: fokus qopqoni, `Esc`,
 * skroll qulfi va fonni bosib yopish u yerda allaqachon bor va bu yerda
 * ikkinchi marta yozilmaydi. U telefonda PASTDAN chiqadi — menyuni ochgan
 * tugma ham pastda, ya'ni qo'l ekran bo'ylab yurmaydi.
 *
 * Ro'yxat TO'LIQ: pastki qatordagi to'rttasi ham shu yerda qoladi.
 * "Qoldiq" ro'yxat qilingan bo'lsa, bo'limni qidirayotgan odam uni ikki
 * joydan qidirishi kerak bo'lardi.
 */
export const MobileNavDrawer = ({
  open,
  onClose,
  can,
  activePath = '',
  badges = {},
  hasSubdivision = false,
  onNavigate,
  userName,
  userRole,
  onOpenSignature,
  onLogout,
}: MobileNavDrawerProps) => {
  const visible = visibleNavItems(can, hasSubdivision);
  const activeTo = activeNavTo(visible, activePath);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>, to: string): void => {
    if (!isPlainLeftClick(event)) return;
    if (onNavigate !== undefined) {
      event.preventDefault();
      onNavigate(to);
    }
    /* Menyu O'ZI yopiladi: yangi ekran ustida eski menyu turib qolsa,
       odam bir ish uchun ikki marta bosishi kerak bo'lardi. */
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t('tabbar.drawerTitle')}
      closeLabel={t('tabbar.close')}
      className="[&>header]:px-4 [&>header]:py-4"
    >
      <div className="flex flex-col gap-4 px-4 pb-4">
        <nav aria-label={t('sidebar.label')}>
          <ul className="flex flex-col gap-0.5">
            {visible.map((item) => {
              const label = t(item.label);
              const count = badges[item.to] ?? 0;
              const Icon = item.icon;
              const active = item.to === activeTo;

              return (
                <li key={item.to}>
                  <a
                    href={navEntry(item)}
                    aria-label={count > 0 ? `${label} (${count})` : label}
                    aria-current={active ? 'page' : undefined}
                    onClick={(event) => handleClick(event, navEntry(item))}
                    className={cn(
                      'flex h-[var(--size-control)] items-center gap-3 px-3',
                      'rounded-[var(--radius-sm)] text-sm font-medium',
                      active
                        ? 'bg-accent-soft text-accent'
                        : 'text-text-muted hover:bg-surface-2 hover:text-text',
                    )}
                  >
                    <Icon size={18} aria-hidden className="shrink-0" />
                    <span className="truncate">{label}</span>
                    {count > 0 ? (
                      <Badge tone="danger" mono className="ml-auto">
                        {count}
                      </Badge>
                    ) : null}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Hisob amallari shu yerda: telefonda yuqori qatorda ular uchun
            joy yo'q — u yerda bildirishnoma va qidiruv siqilib turibdi. */}
        <div className="flex flex-col gap-0.5 border-t border-border pt-3">
          <div className="px-3 pb-2">
            <p className="truncate text-sm font-semibold text-text">{userName ?? '—'}</p>
            <p className="truncate text-xs text-text-faint">{userRole}</p>
          </div>

          <button
            type="button"
            onClick={onOpenSignature}
            className={cn(
              'flex h-[var(--size-control)] items-center gap-3 px-3',
              'rounded-[var(--radius-sm)] text-left text-sm font-medium text-text-muted',
              'hover:bg-surface-2 hover:text-text',
            )}
          >
            <PenLine size={18} aria-hidden className="shrink-0" />
            {t('signature.mine')}
          </button>

          <button
            type="button"
            onClick={onLogout}
            className={cn(
              'flex h-[var(--size-control)] items-center gap-3 px-3',
              'rounded-[var(--radius-sm)] text-left text-sm font-medium text-text-muted',
              'hover:bg-surface-2 hover:text-danger',
            )}
          >
            <LogOut size={18} aria-hidden className="shrink-0" />
            {t('topbar.logout')}
          </button>
        </div>
      </div>
    </Drawer>
  );
};
